/**
 * Owners, record scope, assignment rotations and assigning existing
 * people, as TanStack Query hooks.
 *
 * A rotation is a team's route group on a stream: the routing engine picks
 * the rep exactly as it picks a buyer, and never sells. Its reps are
 * destinations of their own kind, kept off the Destinations page and built
 * here instead.
 *
 * Changing an owner touches the person, company or deal page, every list
 * that shows an owner, and the timelines, so each write invalidates them.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

type S = components['schemas'];
export type AssignmentGroup = S['AssignmentGroup'];
export type AssignmentRep = S['AssignmentRep'];
export type AssignmentRepInput = S['AssignmentRepInput'];
export type ActiveHours = S['ActiveHours'];
export type AssignmentJob = S['AssignmentJob'];
export type AssignmentJobDetail = S['AssignmentJobDetail'];
export type BulkOwnerResult = S['BulkOwnerResult'];
export type CrmAccessSettings = S['CrmAccessSettings'];

/** How a rotation picks its rep, in words. */
export const METHODS: { value: string; label: string; hint: string }[] = [
  { value: 'round-robin', label: 'Take turns', hint: 'Each rep in turn, skipping anyone off or at a cap.' },
  { value: 'weighted', label: 'By weight', hint: 'More to the reps with a higher weight.' },
  { value: 'priority', label: 'In order', hint: 'The first rep who can take it, then the next.' },
];

function useInvalidateOwners() {
  const qc = useQueryClient();
  return () => {
    for (const key of ['people', 'person', 'companies', 'company', 'deals', 'deal', 'deals-summary', 'timeline', 'tasks']) {
      qc.invalidateQueries({ queryKey: [key] });
    }
  };
}

/* ---------- record scope ---------- */

export function useCrmAccess(enabled = true) {
  return useQuery({
    queryKey: ['settings', 'crm-access'],
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/crm-access', {}), 'CrmAccessSettings'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveCrmAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CrmAccessSettings) =>
      unwrap(await api.PUT('/api/v1/settings/crm-access', { body }), 'CrmAccessSettings'),
    onSuccess: (data) => qc.setQueryData(['settings', 'crm-access'], data),
  });
}

/* ---------- owners ---------- */

/** Give one person an owner: a user id, `me`, or null for nobody. */
export function useSetPersonOwner() {
  const invalidate = useInvalidateOwners();
  return useMutation({
    mutationFn: async ({ id, owner }: { id: string; owner: string | null }) =>
      unwrap(
        await api.PUT('/api/v1/people/{id}/owner', {
          params: { path: { id } },
          body: { owner_user_id: owner ?? '' },
        }),
        'OwnerView',
      ),
    onSuccess: invalidate,
  });
}

export type OwnedKind = 'people' | 'companies' | 'deals';

const BULK_PATH = {
  people: '/api/v1/people/owner',
  companies: '/api/v1/companies/owner',
  deals: '/api/v1/deals/owner',
} as const;

/** Assign owner on many records at once (assign_records). */
export function useBulkOwner(kind: OwnedKind) {
  const invalidate = useInvalidateOwners();
  return useMutation({
    mutationFn: async ({ ids, owner }: { ids: string[]; owner: string | null }) =>
      unwrap(
        await api.POST(BULK_PATH[kind], { body: { ids, owner_user_id: owner ?? '' } }),
        'BulkOwnerResult',
      ),
    onSuccess: invalidate,
  });
}

/* ---------- rotations ---------- */

const GROUPS = ['assignment', 'groups'];

export function useAssignmentGroups(enabled = true) {
  return useQuery({
    queryKey: GROUPS,
    queryFn: async () => unwrap(await api.GET('/api/v1/assignment/groups', {}), 'AssignmentGroupList'),
    enabled,
    staleTime: 30 * 1000,
  });
}

function useInvalidateGroups() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: GROUPS });
    // A rotation is a tier on its stream's delivery tab.
    qc.invalidateQueries({ queryKey: ['campaign'] });
    // And a route: the stream now assigns, and the Routes page lists it.
    qc.invalidateQueries({ queryKey: ['campaigns'] });
    qc.invalidateQueries({ queryKey: ['routes'] });
  };
}

export function useCreateAssignmentGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: async (body: S['CreateAssignmentGroupBody']) =>
      unwrap(await api.POST('/api/v1/assignment/groups', { body }), 'AssignmentGroup'),
    onSuccess: invalidate,
  });
}

export function useUpdateAssignmentGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: S['UpdateAssignmentGroupBody'] }) =>
      unwrap(
        await api.PATCH('/api/v1/assignment/groups/{id}', { params: { path: { id } }, body }),
        'AssignmentGroup',
      ),
    onSuccess: invalidate,
  });
}

export function useSetAssignmentReps() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: async ({ id, reps }: { id: string; reps: AssignmentRepInput[] }) =>
      unwrap(
        await api.PUT('/api/v1/assignment/groups/{id}/reps', { params: { path: { id } }, body: { reps } }),
        'AssignmentGroup',
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteAssignmentGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/assignment/groups/{id}', { params: { path: { id } } });
      if (res.error !== undefined) {
        throw new Error((res.error as { detail?: string }).detail ?? 'Delete failed');
      }
      return id;
    },
    onSuccess: invalidate,
  });
}

/** A rep as the editor holds them, from what the server sent. */
export function repInput(r: AssignmentRep): AssignmentRepInput {
  return {
    user_id: r.user_id,
    active: r.active,
    priority: r.priority,
    weight: r.weight,
    daily_cap: r.caps.daily_cap,
    weekly_cap: r.caps.weekly_cap,
    monthly_cap: r.caps.monthly_cap,
    active_days: r.active_days,
    active_hours: r.active_hours ?? null,
  };
}

/* ---------- assigning existing people ---------- */

/** Send people through a rotation. Queued; follow it with useAssignmentJob. */
export function useAssignPeople() {
  return useMutation({
    mutationFn: async (body: S['AssignPeopleBody']) =>
      unwrap(await api.POST('/api/v1/assignments', { body }), 'AssignmentJob'),
  });
}

/** One job, polled every second until it finishes. */
export function useAssignmentJob(id: string | null) {
  const invalidate = useInvalidateOwners();
  return useQuery({
    queryKey: ['assignment', 'job', id],
    queryFn: async () => {
      const d = unwrap(
        await api.GET('/api/v1/assignments/{id}', { params: { path: { id: id! } } }),
        'AssignmentJobDetail',
      );
      if (d.job.status === 'done' || d.job.status === 'failed') invalidate();
      return d;
    },
    enabled: !!id,
    refetchInterval: (q) => {
      const s = q.state.data?.job.status;
      return s === 'done' || s === 'failed' ? false : 1000;
    },
  });
}
